import Section from '@/components/common/Section';
import LogoStrip from '@/components/visuals/LogoStrip';
import { motion } from 'framer-motion';
import { fadeUp, staggerContainer } from '@/animations/variants';

const platforms = [
  { name: "Microsoft Azure", icon: "/images/Azure-Logo.png", href: "/images/Azure-Logo.png" },
  { name: "AWS", icon: "/images/AWS-Emblem.png", href: "https://aws.amazon.com" },
  { name: "Google Cloud", icon: "/images/google-cloud-logo.png", href: "https://cloud.google.com" },
];

const developmentStack = [
  {
    category: "Frontend",
    technologies: ["React", "React Native", "TypeScript", "Next.js"],
    icon: "🎨",
  },
  {
    category: "Backend",
    technologies: ["C#", "Java", "Python", "Node.js"],
    icon: "⚙️",
  },
  {
    category: "Databases",
    technologies: ["SQL Server", "PostgreSQL", "MongoDB", "CosmosDB"],
    icon: "🗄️",
  },
  {
    category: "DevOps",
    technologies: ["Docker", "Kubernetes", "Azure DevOps", "GitHub Actions"],
    icon: "🔄",
  },
];

const capabilities = [
  {
    title: "Natural Language Processing",
    description: "Advanced NLP solutions for text analysis, sentiment analysis, and language understanding.",
    icon: "💬",
    features: ["Text Analytics", "Chatbots", "Document Processing", "Translation Services"]
  },
  {
    title: "Voice Processing",
    description: "Speech recognition, voice synthesis, and voice-enabled applications.",
    icon: "🎤",
    features: ["Speech-to-Text", "Text-to-Speech", "Voice Commands", "Audio Analytics"]
  },
  {
    title: "Intelligent Automation",
    description: "AI-powered automation solutions for business processes and workflows.",
    icon: "🤖",
    features: ["RPA", "Workflow Automation", "Smart Routing", "Decision Engines"]
  },
  {
    title: "AI Agents & Assistants",
    description: "Intelligent virtual assistants and AI agents for customer service and support.",
    icon: "🧠",
    features: ["Virtual Assistants", "Knowledge Bases", "Smart Responses", "Learning Systems"]
  },
  {
    title: "Business Intelligence",
    description: "Data visualization, analytics, and business intelligence solutions.",
    icon: "📊",
    features: ["Power BI", "Custom Dashboards", "Data Analytics", "Reporting Systems"]
  },
  {
    title: "Machine Learning",
    description: "Custom ML models and predictive analytics solutions.",
    icon: "🔮",
    features: ["Predictive Models", "Classification", "Recommendation Systems", "Anomaly Detection"]
  },
];

export default function TechnologyPage() {
  return (
    <>
      {/* Technology Overview */}
      <Section
        title="Our Technology Stack"
        subtitle="Modern, scalable, and enterprise-ready technology solutions"
        centered
        background="gradient"
      >
        <motion.p 
          className="text-lg text-textSecondary max-w-4xl mx-auto text-center"
          variants={fadeUp}
        >
          We leverage cutting-edge technologies and proven frameworks to build robust, 
          scalable solutions that grow with your business. Our technology choices are 
          driven by reliability, performance, and long-term maintainability.
        </motion.p>
      </Section>

      {/* Cloud Platforms */}
      <Section
        title="Cloud Platforms"
        subtitle="Primary focus on Microsoft Azure with multi-cloud expertise"
        centered
        background="surface"
      >
        <LogoStrip logos={platforms} />
        <motion.div 
          className="mt-12 bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20 rounded-xl p-8 text-center"
          variants={fadeUp}
        >
          <h3 className="text-xl font-heading font-semibold text-textPrimary mb-4">
            Microsoft Azure - Our Primary Platform
          </h3>
          <p className="text-textSecondary max-w-3xl mx-auto">
            We have deep expertise in Microsoft Azure ecosystem, including Azure Functions, 
            App Services, Cognitive Services, and Azure AI. Our team is certified in Azure 
            architecture and can help you leverage the full power of Microsoft's cloud platform.
          </p>
        </motion.div>
      </Section>

      {/* Development Stack */}
      <Section
        title="Development Stack"
        subtitle="Full-stack expertise across modern development frameworks"
        centered
      >
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          variants={staggerContainer}
        >
          {developmentStack.map((stack, index) => (
            <motion.div key={index} variants={fadeUp}>
              <div className="bg-surface border border-border rounded-xl p-6 h-full hover:border-primary/50 transition-all duration-normal">
                <div className="text-6xl mb-4 text-center">{stack.icon}</div>
                <h3 className="text-lg font-heading font-semibold text-textPrimary mb-3 text-center">
                  {stack.category}
                </h3>
                <div className="space-y-2">
                  {stack.technologies.map((tech, techIndex) => (
                    <div 
                      key={techIndex}
                      className="text-sm text-white bg-[#162660] px-3 py-1 rounded-full text-center"
                    >
                      {tech}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </Section>

      {/* AI & Automation Capabilities */}
      <Section
        title="AI & Automation Capabilities"
        subtitle="Advanced AI solutions for modern business challenges"
        centered
        background="surface"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {capabilities.map((capability, index) => (
            <motion.div
              key={index}
              className="bg-background border border-border rounded-xl p-6 hover:border-primary/50 transition-all duration-normal"
              variants={fadeUp}
              whileHover={{ y: -5 }}
            >
              <div className="text-6xl mb-4">{capability.icon}</div>
              <h3 className="text-xl font-heading font-semibold text-textPrimary mb-3">
                {capability.title}
              </h3>
              <p className="text-textSecondary mb-4 leading-relaxed">
                {capability.description}
              </p>
              
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-textPrimary uppercase tracking-wider">
                  Features
                </h4>
                <div className="flex flex-wrap gap-1">
                  {capability.features.map((feature, featureIndex) => (
                    <span
                      key={featureIndex}
                      className="text-xs px-2 py-1 bg-primary/10 text-primary rounded"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* GitHub Copilot Integration */}
      <Section centered>
        <motion.div 
          className="bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20 rounded-xl p-8 md:p-12 text-center"
          variants={fadeUp}
        >
          <div className="text-6xl mb-6">🤖✨</div>
          <h2 className="text-2xl md:text-3xl font-heading font-semibold text-textPrimary mb-4">
            Enhanced with GitHub Copilot
          </h2>
          <p className="text-lg text-textSecondary max-w-3xl mx-auto mb-6">
            Our development teams leverage GitHub Copilot and AI-powered development tools 
            to accelerate coding, improve code quality, and enhance productivity. This allows 
            us to deliver faster while maintaining high standards of code excellence.
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <span className="px-4 py-2 bg-primary/20 text-primary rounded-full">
              Code Generation
            </span>
            <span className="px-4 py-2 bg-primary/20 text-primary rounded-full">
              Code Review
            </span>
            <span className="px-4 py-2 bg-primary/20 text-primary rounded-full">
              Documentation
            </span>
            <span className="px-4 py-2 bg-primary/20 text-primary rounded-full">
              Testing
            </span>
          </div>
        </motion.div>
      </Section>

      {/* Call to Action */}
      <Section centered background="surface">
        <motion.div 
          className="text-center"
          variants={fadeUp}
        >
          <h2 className="text-3xl md:text-4xl font-heading font-semibold text-textPrimary mb-6">
            Let's Discuss Your Technology Needs
          </h2>
          <p className="text-lg text-textSecondary mb-8 max-w-2xl mx-auto">
            Whether you need to modernize existing systems or build new solutions from scratch, 
            our technology expertise can help you achieve your goals efficiently and effectively.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-lg font-heading font-semibold text-lg shadow-lg hover:shadow-glow transition-all duration-normal">
              Technical Consultation
            </button>
            <button className="border-2 border-primary text-primary hover:bg-primary hover:text-white px-8 py-4 rounded-lg font-heading font-semibold text-lg transition-all duration-normal">
              View Architecture
            </button>
          </div>
        </motion.div>
      </Section>
    </>
  );
}